/**
 * NO BROWSER PREFILL (owner, 2026-10-20: "Please make sure browser prefill suggestion should not be there").
 *
 * ⚠ `autocomplete="off"` IS NOT ENOUGH IN CHROME. Chrome ignores it on fields it recognises as a name or an address, which is
 * why the clinic form arrived already filled while every field said "off". What Chrome does honour is a value it does not
 * recognise, so every text field and every form gets one. A MutationObserver covers fields that appear later (each screen is
 * drawn after the route loads, and dialogs open on demand), so no template has to remember to do it.
 *
 * A field that genuinely wants the browser's help can opt out with `data-allow-autofill`.
 *
 * ⚠ IT WATCHES THE APP'S OWN HOST ELEMENT, NOT THE WHOLE DOCUMENT (review MR !1 #8), exactly as the patient site's copy does.
 *
 * The same file exists in the patient site (`projects/patient-web/src/app/no-browser-autofill.ts`); keep the two in step.
 * The ONE deliberate difference: this portal opens ng-bootstrap dialogs (fourteen components use NgbModal), which are
 * attached to <body> beside the app element rather than inside it, so this copy also watches each direct child of <body>.
 * The patient site opens no such dialog, so its copy watches the host alone.
 */
export const NO_AUTOFILL_VALUE = 'lm-no-autofill';

const SKIPPED_TYPES = new Set(['checkbox', 'radio', 'file', 'hidden', 'submit', 'button', 'reset', 'range', 'color', 'image']);

const applyTo = (element: Element): void => {
    if (element.hasAttribute('data-allow-autofill')) {
        return;
    }
    if (element instanceof HTMLFormElement) {
        element.setAttribute('autocomplete', 'off');
        return;
    }
    if (element instanceof HTMLInputElement && SKIPPED_TYPES.has(element.type)) {
        return;
    }
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
        if (element.getAttribute('autocomplete') !== NO_AUTOFILL_VALUE) {
            element.setAttribute('autocomplete', NO_AUTOFILL_VALUE);
        }
    }
};

const sweep = (root: ParentNode): void => {
    if (root instanceof Element) {
        applyTo(root);
    }
    root.querySelectorAll('input, textarea, form').forEach(applyTo);
};

/** Applies the rule to `root` now and to anything added inside it later. Returns a function that stops it. */
const watch = (root: Element): (() => void) => {
    sweep(root);
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof Element) {
                    sweep(node);
                }
            });
        }
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
};

/**
 * Starts the rule for the app (review MR !1 #8: scoped, not the whole document).
 *
 * It watches the app's own element, plus each dialog root that is attached straight to <body> beside it - ng-bootstrap
 * puts a modal there, outside the app element, so watching only the host would let a dialog's fields be prefilled again.
 * <body> itself is watched for its direct children only, and each dialog's watcher stops when the dialog is removed.
 * The returned function stops all of it (the app calls it when it is destroyed).
 */
export const startNoBrowserAutofill = (doc: Document, host: Element): (() => void) => {
    if (!doc || !doc.body || !host || typeof MutationObserver === 'undefined') {
        return () => undefined;
    }
    const stopHost = watch(host);
    const overlays = new Map<Element, () => void>();
    const watchOverlay = (node: Element): void => {
        if (node !== host && !node.contains(host) && !overlays.has(node)) {
            overlays.set(node, watch(node));
        }
    };
    Array.from(doc.body.children).forEach(watchOverlay);
    const bodyObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof Element) {
                    watchOverlay(node);
                }
            });
            mutation.removedNodes.forEach((node) => {
                if (node instanceof Element) {
                    overlays.get(node)?.();
                    overlays.delete(node);
                }
            });
        }
    });
    bodyObserver.observe(doc.body, { childList: true });
    return () => {
        stopHost();
        bodyObserver.disconnect();
        overlays.forEach((stop) => stop());
        overlays.clear();
    };
};
